import { Dispatch, SetStateAction, useState } from 'react'
import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult
} from '@tanstack/react-query'
import { nfdSearchV2, NfdSearchV2Params, NfdV2SearchRecords } from 'api/api-client'
import { ICustomError } from 'api/customError'

export type InfiniteSearchQueryResult = {
  data: NfdV2SearchRecords
  nextPage: number | null
}

export type InfiniteSearchResult = {
  query: UseInfiniteQueryResult<InfiniteSearchQueryResult, ICustomError>
  total: number
  fetchedPages: Set<number>
  setFetchedPages: Dispatch<SetStateAction<Set<number>>>
}

// this makes `limit` a required param
interface UseInfiniteSearchParams extends NfdSearchV2Params {
  limit: number
}

interface UseInfiniteSearch {
  queryKey: QueryKey
  params: UseInfiniteSearchParams
  options?: UseInfiniteQueryOptions<InfiniteSearchQueryResult, ICustomError>
}

export default function useInfiniteSearch({
  queryKey,
  params,
  options = {}
}: UseInfiniteSearch): InfiniteSearchResult {
  const [fetchedPages, setFetchedPages] = useState(new Set<number>())

  const { limit, ...rest } = params

  const fetchPage = async ({ pageParam = 1 }) => {
    setFetchedPages((prev) => new Set(prev.add(pageParam - 1)))

    const offset = (pageParam - 1) * limit

    const { data } = await nfdSearchV2({
      ...rest,
      limit,
      offset
    })

    const nextPage = Array.isArray(data?.nfds) && data.nfds.length === limit ? pageParam + 1 : null

    return {
      data,
      nextPage
    }
  }

  const query = useInfiniteQuery<InfiniteSearchQueryResult, ICustomError>({
    queryKey,
    queryFn: fetchPage,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    retry: (failureCount, error) => {
      if (error.statusCode === 404) {
        return false
      }

      return failureCount < 3
    },
    ...options
  })

  const total = query.data?.pages[query.data.pages.length - 1].data.total || 0

  return {
    query,
    total,
    fetchedPages,
    setFetchedPages
  }
}
